/**
 * Status → KPI bucket mapping for the Departments pages.
 *
 * Three buckets shown as KPI cards:
 *   - Not Started — work that hasn't begun (intake + ready)
 *   - In Progress — actively being worked on (spec → testing/go-live)
 *   - Complete    — shipped
 *
 * Postponed / declined intentionally drop out of all three. They're
 * not "not started" (they were started or declined explicitly) and
 * they're not "in progress" or "complete." Shown separately if/when
 * we add a fourth card later.
 */

import type { Project } from "@/types/project";

export interface KpiBuckets {
  notStarted: number;
  inProgress: number;
  complete: number;
  /** Total of the three buckets above. Excludes postponed/declined. */
  active: number;
  /** Postponed + declined + anything unrecognized — surfaced separately. */
  other: number;
}

const NOT_STARTED = new Set([
  "requested",
  "approved",
  "ready_for_development",
]);

const IN_PROGRESS = new Set([
  "functional_spec",
  "technical_spec",
  "in_progress",
  "testing_go_live",
]);

const COMPLETE = new Set(["complete"]);

export function kpiBucketsFromProjects(projects: Project[]): KpiBuckets {
  let notStarted = 0;
  let inProgress = 0;
  let complete = 0;
  let other = 0;
  for (const p of projects) {
    const s = (p.status ?? "").toLowerCase();
    if (NOT_STARTED.has(s)) notStarted++;
    else if (IN_PROGRESS.has(s)) inProgress++;
    else if (COMPLETE.has(s)) complete++;
    else other++;
  }
  return {
    notStarted,
    inProgress,
    complete,
    active: notStarted + inProgress + complete,
    other,
  };
}
